import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { listPrograms } from "@/lib/apiClient";
import { useI18n } from "@/contexts/I18nContext";
import { resolveProgramName, resolveProgramTagline, resolveProgramCategory } from "@/lib/i18nContent";
import { ArrowRight, BadgeCheck, CheckCircle2, Sparkles } from "lucide-react";

export default function Programs() {
  const { t } = useI18n();
  const [programs, setPrograms] = useState([]);
  useEffect(() => { listPrograms().then(setPrograms); }, []);

  const free = programs.filter((p) => p.is_free);
  const paid = programs.filter((p) => !p.is_free);

  return (
    <div className="min-h-screen flex flex-col" data-testid="programs-page">
      <Header />
      <main className="flex-1 max-w-[1280px] mx-auto w-full px-5 sm:px-8 py-12">
        <div className="text-xs uppercase tracking-[0.22em] text-[#4B5563]">{t("programs_page.eyebrow")}</div>
        <h1 className="font-heading text-3xl sm:text-4xl font-semibold mt-2 text-[#1F2937]">{t("programs_page.title")}</h1>
        <p className="text-[#4B5563] mt-3 max-w-2xl">{t("programs_page.subtitle")}</p>

        <Section title={t("programs_page.free_section")} icon={<Sparkles className="w-4 h-4 text-[#D97706]" />}>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {free.map((p) => <Card key={p.id} p={p} />)}
          </div>
        </Section>

        <Section title={t("programs_page.paid_section")} icon={<BadgeCheck className="w-4 h-4 text-[#064E3B]" />}>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {paid.map((p) => <Card key={p.id} p={p} />)}
          </div>
        </Section>

        <div className="mt-16 rounded-3xl bg-[#064E3B] text-[#FDFBF7] p-8 flex flex-wrap items-center gap-6 justify-between">
          <div>
            <div className="text-xs uppercase tracking-[0.22em] text-[#FED7AA]">{t("programs_page.coming_soon")}</div>
            <div className="font-heading text-xl mt-2">{t("programs_page.coming_soon_title")}</div>
          </div>
          <a href="https://hikmahverses.com" target="_blank" rel="noreferrer"
             className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-[#D97706] hover:bg-[#B45309] text-white font-medium transition">
            {t("programs_page.follow_updates")} <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      </main>
      <Footer />
    </div>
  );
}

function Section({ title, icon, children }) {
  return (
    <section className="mt-10">
      <div className="flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-[#4B5563] mb-4">
        {icon} {title}
      </div>
      {children}
    </section>
  );
}

function Card({ p }) {
  const { t } = useI18n();
  return (
    <Link to={`/programs/${p.id}`} data-testid={`program-card-${p.id}`}
      className="group rounded-2xl bg-white border border-[#E7E0D2] overflow-hidden hover:border-[#064E3B] hover:shadow-[0_15px_40px_-20px_rgba(6,78,59,0.25)] transition">
      <div className="aspect-[16/10] bg-gradient-to-br from-[#ECFDF5] to-[#FDFBF7] overflow-hidden">
        {p.image
          ? <img src={p.image} alt={p.title} className="w-full h-full object-cover group-hover:scale-[1.03] transition duration-500" loading="lazy" onError={(e) => { e.currentTarget.style.display = "none"; }} />
          : <div className="w-full h-full grid place-items-center text-[#4B5563]">No image</div>}
      </div>
      <div className="p-5">
        <div className="flex items-center justify-between text-[10px] uppercase tracking-widest text-[#4B5563]">
          <span>{resolveProgramCategory(t, p)}</span>
          <span className={p.is_free ? "text-[#10B981]" : "text-[#D97706] font-medium"}>{p.is_free ? t("programs_page.free_pill") : `$${p.price}`}</span>
        </div>
        <div className="font-heading text-lg mt-2 text-[#1F2937]">{resolveProgramName(t, p)}</div>
        <div className="text-sm text-[#4B5563] mt-1.5 line-clamp-2">{resolveProgramTagline(t, p)}</div>
      </div>
    </Link>
  );
}
